import { authGeneration, authSignal, reportSessionExpired } from '../auth/session'
import { isRole, type Role } from '../lib/roles'

/** One row of `GET /api/members`. */
export interface Member {
  userId: string
  email: string
  role: Role
  joinedAt: string
}

export type InviteStatus = 'pending' | 'expired' | 'revoked' | 'used'

/** One row of `GET /api/invites`: an invitation a company has sent. */
export interface Invite {
  id: string
  email: string
  role: Role
  inviterEmail: string
  createdAt: string
  expiresAt: string
  status: InviteStatus
}

/** One row of `GET /api/invitations`: an invitation the signed-in user has received. */
export interface Invitation {
  id: string
  companyId: string
  companyName: string
  role: Role
  inviterEmail: string
  expiresAt: string
}

/** `POST /api/invite-links/lookup`: what an invite link shows before anyone signs in. */
export interface InviteLink {
  status: InviteStatus
  inviteId: string
  companyId: string
  companyName: string
  role: Role
  inviterEmail: string
  maskedEmail: string
}

/** The route codes a refusal carries. They stay plain route strings, outside `AUTH_ERROR_CODES`. */
export type RefusalCode =
  | 'last_owner'
  | 'invalid_role'
  | 'invalid_email'
  | 'already_member'
  | 'already_invited'
  | 'invite_expired'
  | 'invite_revoked'
  | 'invite_used'
  | 'invites_not_open'
  | 'invalid_days'
  | 'invalid_label'
  | 'share_links_not_open'
  | 'company_deleted'

const REFUSAL_CODES: readonly string[] = [
  'last_owner', 'invalid_role', 'invalid_email', 'already_member', 'already_invited',
  'invite_expired', 'invite_revoked', 'invite_used', 'invites_not_open',
  'invalid_days', 'invalid_label', 'share_links_not_open', 'company_deleted',
]

/**
 * How a call ended. `send` only classifies, and reports nothing but a `401`: what a `404`
 * means depends on the caller (design, "Lost access").
 *
 * - `stale`: the session's generation moved on while the request was out; drop the answer.
 * - `expired`: a `401`, already reported through `reportSessionExpired()`.
 * - `notFound`: a `404`. On a list it means "not a member"; on a mutation the target may
 *   be gone while the caller still belongs.
 * - `forbidden`: the role `403`.
 * - `refused`: a `400`, `409`, `410` or `503` with one of the route codes above.
 * - `unreachable`: the request's outcome is unknown: a network error, the deadline, or a
 *   `503` without a route code. A write may have happened.
 * - `unavailable`: an answer arrived but was not the contract.
 */
export type Outcome<T> =
  | { status: 'ok'; body: T }
  | { status: 'stale' }
  | { status: 'expired' }
  | { status: 'notFound' }
  | { status: 'forbidden' }
  | { status: 'refused'; code: RefusalCode }
  | { status: 'unreachable' }
  | { status: 'unavailable' }

/** How long one call may take before its outcome is treated as unknown. */
export const MEMBERS_TIMEOUT_MS = 15_000

export async function send<T>(url: string, init: RequestInit, parse: (body: unknown) => T | null): Promise<Outcome<T>> {
  const generation = authGeneration()
  let response: Response
  let text: string
  try {
    response = await fetch(url, {
      credentials: 'same-origin',
      signal: AbortSignal.any([authSignal(), AbortSignal.timeout(MEMBERS_TIMEOUT_MS)]),
      ...init,
    })
    text = await response.text()
  } catch {
    return generation === authGeneration() ? { status: 'unreachable' } : { status: 'stale' }
  }
  if (generation !== authGeneration()) return { status: 'stale' }
  if (response.status === 401) {
    reportSessionExpired()
    return { status: 'expired' }
  }
  let body: unknown = null
  if (text !== '') {
    try {
      body = JSON.parse(text)
    } catch {
      body = null
    }
  }
  const code = record(body)?.error
  if (typeof code === 'string' && REFUSAL_CODES.includes(code)) return { status: 'refused', code: code as RefusalCode }
  if (response.status === 404) return { status: 'notFound' }
  if (response.status === 403) return { status: 'forbidden' }
  if (response.status === 503) return { status: 'unreachable' }
  if (!response.ok) return { status: 'unavailable' }
  const parsed = parse(body)
  return parsed === null ? { status: 'unavailable' } : { status: 'ok', body: parsed }
}

export const isString = (value: unknown): value is string => typeof value === 'string'
const STATUSES: readonly string[] = ['pending', 'expired', 'revoked', 'used']
const isStatus = (value: unknown): value is InviteStatus => typeof value === 'string' && STATUSES.includes(value)

export function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

export function arrayOf<T>(value: unknown, item: (entry: unknown) => T | null): T[] | null {
  if (!Array.isArray(value)) return null
  const items: T[] = []
  for (const entry of value) {
    const parsed = item(entry)
    if (parsed === null) return null
    items.push(parsed)
  }
  return items
}

function memberOf(value: unknown): Member | null {
  const v = record(value)
  if (v === null) return null
  const { userId, email, role, joinedAt } = v
  return isString(userId) && isString(email) && isRole(role) && isString(joinedAt) ? { userId, email, role, joinedAt } : null
}

function inviteOf(value: unknown): Invite | null {
  const v = record(value)
  if (v === null) return null
  const { id, email, role, inviterEmail, createdAt, expiresAt, status } = v
  if (!isString(id) || !isString(email) || !isRole(role) || !isString(inviterEmail)) return null
  if (!isString(createdAt) || !isString(expiresAt) || !isStatus(status)) return null
  return { id, email, role, inviterEmail, createdAt, expiresAt, status }
}

function invitationOf(value: unknown): Invitation | null {
  const v = record(value)
  if (v === null) return null
  const { id, companyId, companyName, role, inviterEmail, expiresAt } = v
  if (!isString(id) || !isString(companyId) || !isString(companyName) || !isRole(role)) return null
  if (!isString(inviterEmail) || !isString(expiresAt)) return null
  return { id, companyId, companyName, role, inviterEmail, expiresAt }
}

function issuedOf(value: unknown): { invite: Invite; token: string } | null {
  const v = record(value)
  const invite = inviteOf(v?.invite)
  return invite !== null && isString(v?.token) && v.token !== '' ? { invite, token: v.token } : null
}

function linkOf(value: unknown): InviteLink | null {
  const v = record(value)
  if (v === null) return null
  const { status, inviteId, companyId, companyName, role, inviterEmail, maskedEmail } = v
  if (!isStatus(status) || !isString(inviteId) || !isString(companyId) || !isString(companyName)) return null
  if (!isRole(role) || !isString(inviterEmail) || !isString(maskedEmail)) return null
  return { status, inviteId, companyId, companyName, role, inviterEmail, maskedEmail }
}

export const company = (companyId: string) => `company=${encodeURIComponent(companyId)}`
export const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})
export const empty = () => ({})

export function listMembers(companyId: string): Promise<Outcome<Member[]>> {
  return send(`/api/members?${company(companyId)}`, {}, (body) => arrayOf(record(body)?.members, memberOf))
}

export function changeRole(companyId: string, userId: string, role: Role): Promise<Outcome<Member>> {
  return send(
    `/api/members/${encodeURIComponent(userId)}?${company(companyId)}`,
    json('PATCH', { role }),
    (body) => memberOf(record(body)?.member),
  )
}

/** Removes a member, or leaves when `userId` is the caller's own id. */
export function removeMember(companyId: string, userId: string): Promise<Outcome<object>> {
  return send(`/api/members/${encodeURIComponent(userId)}?${company(companyId)}`, { method: 'DELETE' }, empty)
}

/**
 * `DELETE /api/companies` (GRO-130), for an Owner. `204` is `ok`; a repeat of a delete
 * that already committed is `refused` with `company_deleted`.
 */
export function deleteCompany(companyId: string): Promise<Outcome<object>> {
  return send(`/api/companies?${company(companyId)}`, { method: 'DELETE' }, empty)
}

export function listInvites(companyId: string): Promise<Outcome<Invite[]>> {
  return send(`/api/invites?${company(companyId)}`, {}, (body) => arrayOf(record(body)?.invites, inviteOf))
}

export function createInvite(companyId: string, email: string, role: Role): Promise<Outcome<{ invite: Invite; token: string }>> {
  return send(`/api/invites?${company(companyId)}`, json('POST', { email, role }), issuedOf)
}

/** Issues a new link: the answer carries a new invitation id, and the old one is revoked. */
export function resendInvite(companyId: string, id: string): Promise<Outcome<{ invite: Invite; token: string }>> {
  return send(`/api/invites/${encodeURIComponent(id)}/resend?${company(companyId)}`, { method: 'POST' }, issuedOf)
}

export function revokeInvite(companyId: string, id: string): Promise<Outcome<object>> {
  return send(`/api/invites/${encodeURIComponent(id)}?${company(companyId)}`, { method: 'DELETE' }, empty)
}

export function listInvitations(): Promise<Outcome<Invitation[]>> {
  return send('/api/invitations', {}, (body) => arrayOf(record(body)?.invitations, invitationOf))
}

export function acceptInvitation(id: string): Promise<Outcome<{ companyId: string; role: Role }>> {
  return send(`/api/invitations/${encodeURIComponent(id)}/accept`, { method: 'POST' }, (body) => {
    const v = record(body)
    return v && isString(v.companyId) && isRole(v.role) ? { companyId: v.companyId, role: v.role } : null
  })
}

export function declineInvitation(id: string): Promise<Outcome<object>> {
  return send(`/api/invitations/${encodeURIComponent(id)}/decline`, { method: 'POST' }, empty)
}

export type LookupResult =
  | { status: 'ok'; link: InviteLink }
  | { status: 'notFound' }
  | { status: 'unavailable' }

/** The longest `Retry-After` the lookup waits out before it gives up. */
const MAX_RETRY_AFTER_MS = 10_000

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Needs no session, so it carries no generation check: what a link says does not depend
 * on who is signed in. A `429` is retried once after `Retry-After`; a second `429`, a
 * `403 forbidden_origin` or a network failure is the service-unavailable notice.
 */
export async function lookupInviteLink(token: string, sleep: (ms: number) => Promise<void> = wait): Promise<LookupResult> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    let response: Response
    try {
      response = await fetch('/api/invite-links/lookup', {
        credentials: 'same-origin',
        signal: AbortSignal.timeout(MEMBERS_TIMEOUT_MS),
        ...json('POST', { token }),
      })
    } catch {
      return { status: 'unavailable' }
    }
    if (response.status === 429 && attempt === 0) {
      const seconds = Number(response.headers.get('Retry-After'))
      await sleep(Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds * 1000, MAX_RETRY_AFTER_MS) : 1000)
      continue
    }
    if (response.status === 404) return { status: 'notFound' }
    if (!response.ok) return { status: 'unavailable' }
    try {
      const link = linkOf(await response.json())
      return link === null ? { status: 'unavailable' } : { status: 'ok', link }
    } catch {
      return { status: 'unavailable' }
    }
  }
  return { status: 'unavailable' }
}
